import { createSignal } from "solid-js";
import { matchesSendKey, type SendKey } from "@/lib/sendKey";

export type { SendKey };
export type Settings = {
  sendKey: SendKey;
  /**
   * 管理者だけの表示と操作を出すか。off にすると普通のユーザーと同じ見え方になる。
   * 権限そのものは変わらないので、API は off のままでも受け付ける。
   */
  adminMode: boolean;
};

const STORAGE_KEY = "talkers.settings.v1";
const DEFAULT_SETTINGS: Settings = { sendKey: "modifier-enter", adminMode: true };

function load(): Settings {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return DEFAULT_SETTINGS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_SETTINGS;
    return {
      sendKey:
        "sendKey" in parsed && (parsed.sendKey === "enter" || parsed.sendKey === "modifier-enter")
          ? parsed.sendKey
          : DEFAULT_SETTINGS.sendKey,
      adminMode:
        "adminMode" in parsed && typeof parsed.adminMode === "boolean"
          ? parsed.adminMode
          : DEFAULT_SETTINGS.adminMode,
    };
  } catch {
    // 壊れていれば既定に戻す
  }
  return DEFAULT_SETTINGS;
}

const [settings, setSettingsSignal] = createSignal<Settings>(load());
const [settingsOpen, setSettingsOpen] = createSignal(false);

export { settings, settingsOpen, setSettingsOpen };

function save(next: Settings): void {
  setSettingsSignal(next);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function setSendKey(sendKey: SendKey): void {
  save({ ...settings(), sendKey });
}

export function setAdminMode(adminMode: boolean): void {
  save({ ...settings(), adminMode });
}

const isMac = (): boolean => navigator.userAgent.includes("Mac");

export function modifierLabel(): string {
  return isMac() ? "⌘" : "Ctrl";
}

export function sendKeyLabel(): string {
  return settings().sendKey === "enter" ? "Enter" : `${modifierLabel()} + Enter`;
}

export function shouldSend(event: KeyboardEvent): boolean {
  return matchesSendKey(event, settings().sendKey);
}
